import { Box, Paragraph, Spinner, Text, VisuallyHidden } from '@zillow/constellation-10';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import React, { useEffect, useState } from 'react';
import numberUtils from 'app/shared/utils/numberUtils';
import { capitalizeFirstLetter } from 'app/shared/utils/stringUtils';
import DataCard from 'app/shared/components/DataCard';
import LeftAlignedYAxisTick from 'app/shared/components/LeftAlignedYAxisTick';
import ChartTooltip from 'app/shared/components/ChartTooltip';
import Dot from './Dot';

import type { HistoricRentPrice } from 'app/shared/types/HistoricRentPrice';

interface RentLineGraphProps {
  curYear: Array<HistoricRentPrice>;
  prevYear: Array<HistoricRentPrice>;
  yearlyChange: number | null;
}

const BLUE_500 = '#0D4599';
const AQUA_400 = '#059EC2';

const RentLineGraph: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<RentLineGraphProps>>> = (
  props,
) => {
  const { curYear, prevYear, yearlyChange } = props;

  const [isClientLoaded, setIsClientLoaded] = useState(false);
  useEffect(() => {
    setIsClientLoaded(true);
  }, []);

  const data = prevYear.map((medRent, i) => {
    return {
      month: capitalizeFirstLetter(medRent.month),
      prevPrice: medRent.price || null,
      ...(i < curYear.length && { price: curYear[i].price || null }),
    };
  });

  const isIncomplete = {
    curYear: curYear.some((rentPrice: HistoricRentPrice) => rentPrice.price === 0),
    prevYear: prevYear.some((rentPrice: HistoricRentPrice) => rentPrice.price === 0),
  };

  const thisYearLabel = curYear.length ? `This year (${curYear[0].year})` : null;
  const lastYearLabel = prevYear.length ? `Last year (${prevYear[0].year})` : null;

  const AccessibleTable = (
    <VisuallyHidden>
      <table>
        <caption>Average rent price over time</caption>
        <thead>
          <tr>
            <td></td>
            <th scope="col">{lastYearLabel}</th>
            <th scope="col">{thisYearLabel}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((medRent) => {
            const { month, price, prevPrice } = medRent;
            return (
              <tr key={month}>
                <th scope="row">{month}</th>
                <td>{prevPrice ? numberUtils.asCurrency(prevPrice) : 'No data'}</td>
                <td>{price ? numberUtils.asCurrency(price) : 'No data'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </VisuallyHidden>
  );

  return (
    <DataCard
      heading="Average rent price over time"
      copyText={
        yearlyChange ? (
          <>
            In the last year, rent has {yearlyChange > 0 ? 'increased' : 'decreased'} by{' '}
            <strong>{numberUtils.asCurrency(Math.abs(yearlyChange))}</strong> compared to the previous year.
          </>
        ) : (
          ''
        )
      }
    >
      {isClientLoaded ? (
        <>
          <Box aria-hidden css={{ marginTop: '400', marginBottom: '200' }}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data}>
                <XAxis dataKey="month" axisLine={false} tickLine={false} style={{ fontSize: '14px' }} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={LeftAlignedYAxisTick}
                  tickFormatter={(value: number) => numberUtils.asCurrency(value)}
                  style={{ fontSize: '14px' }}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  filterNull={false}
                  content={ChartTooltip}
                  formatter={(formatterProps: any) => {
                    const year = formatterProps.name;
                    const isFutureDate = formatterProps.value === undefined;
                    return isFutureDate ? null : (
                      <>
                        <Text textStyle="body-xs" css={{ marginRight: '200' }} style={{ color: formatterProps.color }}>
                          ▬
                        </Text>
                        <Text textStyle="body-xs" css={{ marginRight: '200' }}>
                          {formatterProps.payload.month} {year}
                        </Text>
                        <strong>
                          {formatterProps.value ? numberUtils.asCurrency(formatterProps.value) : 'No data'}
                        </strong>
                      </>
                    );
                  }}
                  wrapperStyle={{ fontSize: '14px' }}
                />
                <CartesianGrid vertical={false} stroke="#F1F1F4" />
                {prevYear.length && (
                  <Line
                    type="monotone"
                    dataKey="prevPrice"
                    stroke={AQUA_400}
                    name={prevYear[0].year}
                    strokeWidth="3"
                    dot={isIncomplete.prevYear ? { fill: AQUA_400 } : false}
                    connectNulls={isIncomplete.prevYear && true}
                    isAnimationActive={false}
                  />
                )}
                {curYear.length && (
                  <Line
                    type="monotone"
                    dataKey="price"
                    name={curYear[0].year}
                    stroke={BLUE_500}
                    strokeWidth="3"
                    dot={
                      isIncomplete.curYear
                        ? { fill: BLUE_500 }
                        : (dotProps: any) => <Dot {...dotProps} lastIdx={curYear.length - 1} />
                    }
                    connectNulls={isIncomplete.curYear && true}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          {/* recharts Legend component is too buggy to use https://github.com/recharts/recharts/issues/2704 */}
          <Box css={{ display: 'flex', flexDirection: 'row', marginLeft: '1200' }}>
            {thisYearLabel ? (
              <Paragraph textStyle="body-sm" aria-hidden>
                <Text css={{ color: 'text.accent.blue.hero', marginRight: '200' }}>▬</Text>
                {thisYearLabel}
              </Paragraph>
            ) : null}
            {lastYearLabel ? (
              <Paragraph textStyle="body-sm" css={{ marginLeft: '400' }} aria-hidden>
                <Text
                  css={{
                    color:
                      'aqua.400' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change */,
                    marginRight: '200',
                  }}
                >
                  ▬
                </Text>
                {lastYearLabel}
              </Paragraph>
            ) : null}
          </Box>
        </>
      ) : (
        // oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 250px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
        <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '1', height: '250px' }}>
          <Spinner size="sm" />
        </Box>
      )}
      {AccessibleTable}
    </DataCard>
  );
};

export default RentLineGraph;
